import { type ForwardRefRenderFunction, forwardRef, type ReactNode, useMemo, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { MenuItem } from '@mui/material'
import type { SelectInputProps } from '@mui/material/Select/SelectInput'
import { formatDate } from 'commons/utils/date'
import { Tooltip } from 'components'
import { theme } from 'styles/theme/hav-theme'

import QuotaSelectModal from './components/QuotaSelectModal'
import * as S from './styles'

export interface QuotaOptionProps {
  value: string | number
  title: string
  anticipated?: boolean
}

export interface QuotaSelectProps {
  name: string
  label?: string
  labelAdornment?: ReactNode
  value: string
  options: Array<QuotaOptionProps>
  disabled?: boolean
  onChange: SelectInputProps<unknown>['onChange']
  onBlur?: SelectInputProps<unknown>['onBlur']
}

const menuStyle = {
  '& li': {
    ':not(:last-child)': {
      marginBottom: theme.spacing.xs,
    },
    border: `1px solid ${theme.colors.quartenary}`,
    borderRadius: theme.shapes.borderRadius.sm,
  },
  '& ul': {
    padding: 0,
  },
  gap: theme.spacing.sm,
  padding: theme.spacing.sm,
}

const QuotaSelectComponent: ForwardRefRenderFunction<HTMLInputElement, QuotaSelectProps> = (
  { label, name, value, options, disabled = false, onChange, onBlur, labelAdornment },
  ref,
) => {
  const [openRegularModal, setOpenRegularModal] = useState(false)
  const [openAnticipatedModal, setOpenAnticipatedModal] = useState(false)

  const handleQuotaSelection = (option: QuotaOptionProps) => {
    const { anticipated } = option
    const currentOption = options.find((element) => element.value === value)

    if (anticipated && !currentOption?.anticipated) {
      setOpenAnticipatedModal(true)
    } else if (!anticipated && currentOption?.anticipated) {
      setOpenRegularModal(true)
    }
  }

  const hasInactiveValue = useMemo(
    () => !!value && !options.find((option) => option.value === value),
    [value],
  )

  return (
    <S.FormControlStyled fullWidth variant="outlined">
      {!!label && (
        <S.WrapLabelStyled>
          <S.LabelStyled id="labelQuotaSelect">{label}</S.LabelStyled>
          {labelAdornment && labelAdornment}
        </S.WrapLabelStyled>
      )}

      <S.SelectStyled
        disabled={disabled}
        displayEmpty
        height={30}
        IconComponent={ExpandMoreIcon}
        inputRef={ref}
        MenuProps={{
          PaperProps: {
            sx: menuStyle,
          },
        }}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        value={value}
      >
        {!value && (
          <MenuItem disabled key={'empty-option'} value="">
            <S.ItemWrapper>
              <S.ItemLabel>{'Selecione um cliente'}</S.ItemLabel>
            </S.ItemWrapper>
          </MenuItem>
        )}
        {hasInactiveValue && (
          <MenuItem disabled key={'disabled-option'} value={value}>
            <S.ItemWrapper>
              <S.ItemLabel>{formatDate(value, 'MM/YYYY')}</S.ItemLabel>
            </S.ItemWrapper>
          </MenuItem>
        )}
        {options.map((option, key) => {
          return (
            <MenuItem key={key} onClick={() => handleQuotaSelection(option)} value={option.value}>
              <S.ItemWrapper>
                <S.ItemLabel>{option.title}</S.ItemLabel>
                {option.anticipated && (
                  <Tooltip
                    arrow
                    placement="bottom-start"
                    title="O pedido será efetivado automaticamente dentro do mês cota selecionado após análise das áreas produtivas."
                  >
                    <S.SelectTag>
                      <span>Pré-Venda</span>
                    </S.SelectTag>
                  </Tooltip>
                )}
              </S.ItemWrapper>
            </MenuItem>
          )
        })}
      </S.SelectStyled>

      <QuotaSelectModal
        description="O pedido padrão impacta, diretamente, na cota do mês selecionado, e deve cumprir os critérios de cota e estoque para envio ao SAP."
        open={openRegularModal}
        setOpen={setOpenRegularModal}
        title="Você mudou para pedido padrão!"
      />
      <QuotaSelectModal
        description="A pré-venda servirá como uma projeção para sua cota. Ele será enviado ao SAP, somente, no mês selecionado, impactando a sua cota futura."
        open={openAnticipatedModal}
        setOpen={setOpenAnticipatedModal}
        title="Você mudou para pré-venda!"
      />
    </S.FormControlStyled>
  )
}

export const QuotaSelect = forwardRef(QuotaSelectComponent)
